import { projectPath } from "@/utils/projectRoutes";
import {
  HOME_AVATAR_FALLBACK,
  HOME_BANNER_FALLBACK,
  HOME_PROJECT_FALLBACK,
  imageWithFallback,
} from "@/utils/homeImageFallbacks";

function addImageOptimization(imageUrl, context = "default") {
  if (!imageUrl || typeof imageUrl !== "string") return imageUrl;
  if (imageUrl.includes("apizinnes.com.br")) return imageUrl;
  const separator = imageUrl.includes("?") ? "&" : "?";
  const params = {
    banner: "w=1200&q=75&f=webp",
    card: "w=420&q=80&f=webp",
    avatar: "w=160&q=85&f=webp",
    default: "w=420&q=80&f=webp",
  };
  return `${imageUrl}${separator}${params[context] || params.default}`;
}

function normalizeAgeRating(project = {}) {
  const rating = project.ageRating || project.age_rating || {};
  const rawLabel = rating.rating || project.age_rating_rating || project.age_rating_label || "";
  const rawAge = rating.age || project.age_rating_age || "";
  const ageFromLabel = String(rawLabel).replace(/\D/g, "");
  const age = String(rawAge || ageFromLabel || "");
  const label = rawLabel || (age ? `A${age}` : "Livre");

  return {
    label,
    age,
    className: age ? `home-age-rating--${age}` : "home-age-rating--free",
  };
}

export function pickArray(response, key) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.[key])) return response[key];
  if (Array.isArray(response?.data?.[key])) return response.data[key];
  return [];
}

export function normalizeProject(project = {}) {
  const cover = addImageOptimization(project.project_thumb || project.thumb || project.title_thumb, "card");
  const banner = addImageOptimization(project.project_banner || project.banner_image || project.image, "banner");
  const ageRating = normalizeAgeRating(project);

  return {
    ...project,
    id: project.id,
    name: project.name || project.title || "Obra Zinnes",
    description: project.description || project.synopsis || project.summary || "",
    authorName: project.author_name || project.user?.name || project.author?.name || "",
    project_thumb: imageWithFallback(cover, HOME_PROJECT_FALLBACK),
    project_banner: imageWithFallback(banner || cover, HOME_BANNER_FALLBACK),
    href: projectPath(project),
    completed: Boolean(project.completed || project.is_completed || project.status === "completed"),
    ageRatingLabel: ageRating.label,
    ageRatingAge: ageRating.age,
    ageRatingClass: ageRating.className,
  };
}

export function normalizeProjects(items = [], options = {}) {
  const normalized = items.map(item => normalizeProject(item, options));
  if (!options.completedOnly) return normalized;
  return normalized.filter(item => item.completed);
}

export function normalizeRanking(items = [], options = {}) {
  const limit = options.limit || items.length;
  return normalizeProjects(items, options).slice(0, limit).map((item, index) => ({
    ...item,
    rank: Number(item.rank || item.position || index + 1),
    showViews: options.showViews !== false,
  }));
}

function firstCount(...values) {
  const value = values.find(candidate => candidate !== undefined && candidate !== null);
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function sumCounts(project, keys) {
  const values = keys.map(key => project[key]).filter(value => value !== undefined && value !== null);
  if (!values.length) return undefined;
  return values.reduce((total, value) => total + firstCount(value), 0);
}

function monthlyEngagement(project = {}) {
  const likes = firstCount(
    project.likes_month_count,
    project.monthly_likes_count,
    sumCounts(project, ["likes_obra_mes", "likes_capitulos_mes"])
  );
  const comments = firstCount(
    project.comments_month_count,
    project.monthly_comments_count,
    sumCounts(project, ["comentarios_obra_mes", "comentarios_capitulos_mes"])
  );

  return { likes, comments, score: likes + comments };
}

function totalEngagement(project = {}) {
  const likes = firstCount(
    project.total_likes,
    project.likes_total,
    project.likes_count_total,
    sumCounts(project, ["likes_obra", "likes_capitulos"])
  );
  const comments = firstCount(
    project.total_comments,
    project.comments_total,
    sumCounts(project, ["comentarios_obra", "comentarios_capitulos"]),
    project.comments_count,
    project.comments
  );

  return { likes, comments, score: likes + comments };
}

function timestamp(value) {
  const parsed = Date.parse(value || "");
  return Number.isFinite(parsed) ? parsed : 0;
}

export function normalizeMonthlyBattle(items = [], options = {}) {
  const limit = options.limit || items.length;
  const scoredItems = normalizeProjects(items, options).map((item, sourceIndex) => {
    const engagement = monthlyEngagement(item);

    return {
      ...item,
      monthly_likes_count: engagement.likes,
      monthly_comments_count: engagement.comments,
      monthly_score: engagement.score,
      sourceIndex,
    };
  });

  return scoredItems
    .sort((left, right) => (
      right.monthly_score - left.monthly_score
      || right.monthly_likes_count - left.monthly_likes_count
      || right.monthly_comments_count - left.monthly_comments_count
      || timestamp(right.updated_at) - timestamp(left.updated_at)
      || Number(right.id || 0) - Number(left.id || 0)
      || left.sourceIndex - right.sourceIndex
    ))
    .slice(0, limit)
    .map((item, index) => {
      const normalized = {
        ...item,
        rank: index + 1,
        showViews: false,
      };
      delete normalized.sourceIndex;
      return normalized;
    });
}

export function normalizeDominaram(items = [], options = {}) {
  const limit = options.limit || items.length;
  const scoredItems = normalizeProjects(items, options).map((item, sourceIndex) => {
    const engagement = totalEngagement(item);

    return {
      ...item,
      total_likes: engagement.likes,
      total_comments: engagement.comments,
      engagement_score: engagement.score,
      sourceIndex,
    };
  });

  return scoredItems
    .sort((left, right) => (
      right.engagement_score - left.engagement_score
      || right.total_likes - left.total_likes
      || right.total_comments - left.total_comments
      || timestamp(right.updated_at) - timestamp(left.updated_at)
      || Number(right.id || 0) - Number(left.id || 0)
      || left.sourceIndex - right.sourceIndex
    ))
    .slice(0, limit)
    .map((item, index) => {
      const normalized = {
        ...item,
        rank: index + 1,
        showViews: false,
      };
      delete normalized.sourceIndex;
      return normalized;
    });
}

export function normalizeHeroSlides(items = []) {
  return items.map((item, index) => {
    const project = normalizeProject(item);
    return {
      ...project,
      eyebrow: item.eyebrow || item.category || "Destaque Zinnes",
      ctaLabel: item.ctaLabel || item.cta_label || "Ler obra",
      image: imageWithFallback(project.project_banner || item.banner_image || item.image, HOME_BANNER_FALLBACK),
      href: item.link || project.href,
      isExternal: Boolean(item.link && /^https?:\/\//i.test(item.link)),
      priority: index === 0 ? "eager" : "lazy",
    };
  });
}

export function normalizeBanners(items = []) {
  return items.map((item, index) => ({
    ...item,
    title: item.title || "Banner Zinnes",
    image: imageWithFallback(addImageOptimization(item.image || item.banner_image, "banner"), HOME_BANNER_FALLBACK),
    priority: index < 2 ? "eager" : "lazy",
  }));
}

export function normalizeAuthors(items = []) {
  return items.map(item => ({
    ...item,
    name: item.name || item.username || "Autor Zinnes",
    profile_photo: imageWithFallback(addImageOptimization(item.profile_photo || item.avatar, "avatar"), HOME_AVATAR_FALLBACK),
    followers: item.follows_count || item.followers_count || item.followers || 0,
    href: item.id ? `/autor/${item.id}` : "/descobrir/categorias",
  }));
}

export function normalizeSearchResults(items = []) {
  return normalizeProjects(items).map(item => ({
    ...item,
    href: projectPath(item),
  }));
}
